/**
 * API Client
 * 
 * 통합 API 클라이언트 - 모든 API 요청을 중앙에서 관리
 * - 인증 토큰 자동 주입
 * - 요청/응답 인터셉터
 * - 에러 처리
 * - 타입 안전한 API 호출
 */

import { API_BASE_URL } from '../config/apiBaseUrl';
import { debugApiLog, isDebugApiLogEnabled } from '../config/debugApiLog';
import { isPublicShoppingBrowsePath } from '../lib/publicShoppingPaths';
import { normalizeBearerTokenForAuthorization } from '../utils/bearerTokenNormalize';
import { serializeJsonRequestBody } from './apiJsonBody';
import { endSessionAndRedirectToLogin, refreshSession } from './authSession';

/**
 * API 에러 타입 정의
 */
export class ApiError extends Error {
  /**
   * true면 React Query 전역 401 핸들러 등에서 로그인 강제 리다이렉트하지 않음 (skipAuth 공개 요청 등)
   */
  skipGlobalLoginRedirect = false;

  constructor(
    public status: number,
    public statusText: string,
    public data?: any,
    message?: string
  ) {
    super(message || `API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/** `REACT_APP_DEBUG_API=true` 일 때 응답 본문만 콘솔 출력 (`apiClient.get` 직후 호출 시 get 로그와 중복될 수 있음) */
export function logResponseData(data: unknown): void {
	if (!isDebugApiLogEnabled() || typeof console === 'undefined' || !console.log) return;
	console.log(data);
}

/** `REACT_APP_DEBUG_API=true` 일 때 에러 본문( `ApiError.data` ) 콘솔 출력 */
export function logErrorResponseData(error: unknown): void {
	if (!isDebugApiLogEnabled() || typeof console === 'undefined' || !console.log) return;
	if (error instanceof ApiError) {
		console.log(error.data);
	} else {
		console.log(error);
	}
}

/** @deprecated 콘솔 출력 제거 — 호출 유지용 no-op */
export function logApiErrorResponseDev(_error: unknown, _label = '[API error body]'): void {}

/**
 * API 응답 타입
 */
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

/**
 * 요청 옵션 타입
 */
export interface RequestOptions extends RequestInit {
  skipAuth?: boolean; // 인증 토큰을 건너뛸지 여부
  timeout?: number; // 타임아웃 (ms)
  params?: Record<string, any>; // Query parameters
}

/**
 * 토큰 저장소 관리
 */
class TokenStorage {
  private readonly ACCESS_TOKEN_KEY = 'medicrew_access_token';
  private readonly REFRESH_TOKEN_KEY = 'medicrew_refresh_token';

  getAccessToken(): string | null {
    const raw = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    if (raw == null || raw === '') return null;
    return normalizeBearerTokenForAuthorization(raw);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, normalizeBearerTokenForAuthorization(token));
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  setTokens(accessToken: string, refreshToken?: string): void {
    this.setAccessToken(accessToken);
    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  /** 액세스 또는 리프레시 중 하나라도 있으면 인증 재시도(401 시 갱신) 가능으로 본다 */
  hasTokens(): boolean {
    return Boolean(this.getAccessToken()?.trim()) || Boolean(this.getRefreshToken()?.trim());
  }
}

const tokenStorage = new TokenStorage();

/**
 * 기본 fetch 래퍼 (타임아웃 지원)
 */
const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeout: number = 30000
): Promise<Response> => {
  const controller = new AbortController();
  let didTimeout = false;
  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeout);

  const upstream = options.signal;
  const onUpstreamAbort = (): void => {
    controller.abort();
  };
  if (upstream) {
    if (upstream.aborted) {
      clearTimeout(timeoutId);
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    upstream.addEventListener('abort', onUpstreamAbort);
  }

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    upstream?.removeEventListener('abort', onUpstreamAbort);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    upstream?.removeEventListener('abort', onUpstreamAbort);
    if (error instanceof Error && error.name === 'AbortError') {
      if (didTimeout) {
        throw new ApiError(408, 'Request Timeout', undefined, '요청 시간이 초과되었습니다.');
      }
      throw error;
    }
    throw error;
  }
};

/**
 * 에러 응답 파싱
 */
const parseErrorResponse = async (response: Response): Promise<any> => {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    return { message: response.statusText };
  } catch {
    return { message: response.statusText };
  }
};

/**
 * Nest 등에서 내려오는 "Cannot DELETE /path" 형태는 사용자에게 기술 메시지로 보이므로 치환합니다.
 */
export function formatUserFacingApiBodyMessage(status: number, rawMessage: string): string {
  const m = rawMessage.trim();
  if (/^Cannot\s+(GET|POST|PUT|PATCH|DELETE)\s+\/[^\s]+$/i.test(m)) {
    if (status === 404) {
      return '요청한 기능을 찾을 수 없습니다. 주소가 없거나 해당 작업을 지원하지 않을 수 있습니다.';
    }
    return '요청을 처리할 수 없습니다. 잠시 후 다시 시도해주세요.';
  }
  return m;
}

function pickNonEmptyString(...vals: unknown[]): string | null {
  for (const v of vals) {
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) return t;
    }
  }
  return null;
}

/**
 * Nest class-validator 등: { property, constraints: { isEmail: "..." } }
 */
function formatConstraintErrorItem(e: Record<string, unknown>): string | null {
  const prop =
    (typeof e.field === 'string' && e.field) ||
    (typeof e.property === 'string' && e.property) ||
    (typeof e.path === 'string' && e.path) ||
    (typeof e.instance === 'string' && e.instance.replace(/^\//, '')) ||
    '';
  const msg = e.message != null ? String(e.message) : '';
  const detailStr = typeof e.detail === 'string' ? e.detail.trim() : '';
  const c = e.constraints;
  if (c && typeof c === 'object' && !Array.isArray(c)) {
    const msgs = Object.values(c as Record<string, unknown>).filter((x): x is string => typeof x === 'string' && x.trim() !== '');
    if (msgs.length) {
      const line = msgs.join(', ');
      return prop ? `${prop}: ${line}` : line;
    }
  }
  if (detailStr) return prop ? `${prop}: ${detailStr}` : detailStr;
  if (prop && msg.trim()) return `${prop}: ${msg.trim()}`;
  if (msg.trim()) return msg.trim();
  return null;
}

function extractErrorsArrayMessage(errors: unknown): string | null {
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const parts = errors.map((e) => {
    if (e == null) return '';
    if (typeof e === 'string') return e.trim();
    if (typeof e !== 'object') return String(e);
    const o = e as Record<string, unknown>;
    const formatted = formatConstraintErrorItem(o);
    if (formatted) return formatted;
    if (o.message != null) return String(o.message);
    return JSON.stringify(e);
  });
  const joined = parts.filter(Boolean).join(' / ');
  return joined.trim() || null;
}

/** { fieldName: ["msg"], nested: { ... } } 형태 */
function extractErrorsObjectMessage(errors: unknown): string | null {
  if (errors == null || typeof errors !== 'object' || Array.isArray(errors)) return null;
  const o = errors as Record<string, unknown>;
  const parts: string[] = [];
  for (const [key, val] of Object.entries(o)) {
    if (Array.isArray(val)) {
      const s = val.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
      if (s) parts.push(`${key}: ${s}`);
    } else if (typeof val === 'string' && val.trim()) {
      parts.push(`${key}: ${val.trim()}`);
    } else if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      const inner = extractErrorsObjectMessage(val);
      if (inner) parts.push(`${key} → ${inner}`);
    }
  }
  const joined = parts.join(' / ');
  return joined.trim() || null;
}

/** 단일 레코드에서 검증 상세만 (message·statusCode 제외) */
function extractValidationDetailsFromRecord(d: Record<string, unknown>): string | null {
  const tryKeys = [
    'errors',
    'validationErrors',
    'violations',
    'fieldErrors',
    'issues',
    'invalidParams',
    'invalidFields',
    'validationMessages',
  ] as const;
  for (const k of tryKeys) {
    const raw = d[k];
    const fromArr = extractErrorsArrayMessage(raw);
    if (fromArr) return fromArr;
    const fromObj = extractErrorsObjectMessage(raw);
    if (fromObj) return fromObj;
  }
  const details = d.details;
  if (Array.isArray(details)) {
    const fromArr = extractErrorsArrayMessage(details);
    if (fromArr) return fromArr;
  }
  if (details != null && typeof details === 'object' && !Array.isArray(details)) {
    const fromObj = extractErrorsObjectMessage(details);
    if (fromObj) return fromObj;
  }
  return null;
}

/** 본문에서 검증 상세만 모음 — 루트와 `data` 중첩 모두 (백엔드가 상세를 data 안에 둔 경우) */
function extractValidationDetailsFromBody(d: Record<string, unknown>): string | null {
  const parts: string[] = [];
  const root = extractValidationDetailsFromRecord(d);
  if (root) parts.push(root);
  const inner = d.data;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    const nested = extractValidationDetailsFromRecord(inner as Record<string, unknown>);
    if (nested) parts.push(nested);
  }
  if (!parts.length) return null;
  const joined = parts.join(' / ');
  return joined.trim() || null;
}

function combineMessageWithDetail(base: string, detail: string | null | undefined): string {
  const d = detail?.trim();
  if (!d) return base;
  if (base.includes(d)) return base;
  return `${base} — ${d}`;
}

/**
 * API JSON 에러 본문에서 사용자용 문장 추출 (Nest / Spring / RFC7807 등).
 * `getErrorMessage`에서 본문 우선, 없을 때만 HTTP 상태별 기본 문구 사용.
 * 검증 실패 시 서버가 일반 `message`만 주고 `errors`·`validationErrors` 등에 필드 상세를 두면 ` — ` 뒤에 붙임.
 */
export function extractUserFacingMessageFromApiBody(status: number, data: unknown): string | null {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  const validationDetail = extractValidationDetailsFromBody(d);

  const m = d.message;
  if (Array.isArray(m) && m.length > 0) {
    const joined = m.filter((x): x is string => typeof x === 'string').join(' / ');
    if (joined.trim()) {
      const base = formatUserFacingApiBodyMessage(status, joined.trim());
      return combineMessageWithDetail(base, validationDetail);
    }
  }
  if (typeof m === 'string' && m.trim()) {
    const base = formatUserFacingApiBodyMessage(status, m.trim());
    return combineMessageWithDetail(base, validationDetail);
  }

  if (validationDetail) {
    return formatUserFacingApiBodyMessage(status, validationDetail);
  }

  const line = pickNonEmptyString(
    d.detail,
    d.description,
    typeof d.title === 'string' ? d.title : null,
    typeof d.error === 'string' ? d.error : null,
    d.msg,
    d.reason,
    d.errorMessage,
    d.error_msg
  );
  if (line) {
    const base = formatUserFacingApiBodyMessage(status, line);
    return combineMessageWithDetail(base, validationDetail);
  }

  if (d.error != null && typeof d.error === 'object' && !Array.isArray(d.error)) {
    const em = (d.error as Record<string, unknown>).message;
    if (typeof em === 'string' && em.trim()) {
      const base = formatUserFacingApiBodyMessage(status, em.trim());
      return combineMessageWithDetail(base, validationDetail);
    }
  }

  const inner = d.data;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return extractUserFacingMessageFromApiBody(status, inner);
  }

  return null;
}

function appendErrorCodeHint(status: number, data: unknown, base: string): string {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return base;
  const d = data as Record<string, unknown>;
  const code = pickNonEmptyString(d.code, d.errorCode, d.error_code);
  if (!code || base.includes(code)) return base;
  return `${base} (오류 코드: ${code})`;
}

/**
 * 표준 message/errors 파싱에 실패했을 때 본문 전체를 짧게 붙여 원인 추적 가능하게 함 (VALIDATION_ERROR 등)
 */
function summarizeApiErrorPayload(data: unknown, maxLen = 1500): string | null {
  if (data == null || data === '') return null;
  if (typeof data === 'string') {
    const t = data.trim();
    if (!t) return null;
    return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
  }
  try {
    const s = JSON.stringify(data);
    if (!s || s === '{}') return null;
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
  } catch {
    return String(data);
  }
}

function appendRawBodyIfMissingParsedMessage(base: string, status: number, data: unknown): string {
  if (status === 401 || status === 403) return base;
  const raw = summarizeApiErrorPayload(data);
  if (!raw) return base;
  if (base.includes(raw.slice(0, Math.min(60, raw.length)))) return base;
  return `${base} — 응답 본문: ${raw}`;
}

/**
 * 에러 메시지 변환 (사용자 친화적)
 */
const getErrorMessage = (error: ApiError): string => {
  const fromBody = error.data != null ? extractUserFacingMessageFromApiBody(error.status, error.data) : null;
  if (fromBody) {
    return appendErrorCodeHint(error.status, error.data, fromBody);
  }

  switch (error.status) {
    case 400:
      return appendRawBodyIfMissingParsedMessage(
        `요청을 처리하지 못했습니다. (HTTP ${error.status}${error.statusText ? ` · ${error.statusText}` : ''})`,
        error.status,
        error.data
      );
    case 401:
      return '인증이 필요합니다. 다시 로그인해주세요.';
    case 403:
      return '접근 권한이 없습니다.';
    case 404:
      return appendRawBodyIfMissingParsedMessage('요청한 리소스를 찾을 수 없습니다.', error.status, error.data);
    case 500:
      return appendRawBodyIfMissingParsedMessage(
        '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        error.status,
        error.data
      );
    case 502:
    case 503:
      return appendRawBodyIfMissingParsedMessage(
        '서비스를 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.',
        error.status,
        error.data
      );
    default:
      return appendRawBodyIfMissingParsedMessage(
        error.message || '알 수 없는 오류가 발생했습니다.',
        error.status,
        error.data
      );
  }
};

/**
 * API 요청 실행 (재시도 로직 포함)
 */
function bodyPreviewForApiLog(options: RequestInit & { body?: BodyInit | null }): string | undefined {
  const b = options.body;
  if (b == null) return undefined;
  if (typeof b === 'string') {
    return b.length > 12_000 ? `${b.slice(0, 12_000)}…(truncated)` : b;
  }
  if (b instanceof FormData) return '[FormData]';
  if (b instanceof ArrayBuffer) return '[ArrayBuffer]';
  if (b instanceof URLSearchParams) {
    const s = b.toString();
    return s.length > 4000 ? `${s.slice(0, 4000)}…` : s;
  }
  if (b instanceof Blob) return '[Blob]';
  return '[body]';
}

const executeRequest = async <T = any>(
  url: string,
  options: RequestOptions,
  retryOn401: boolean = true
): Promise<ApiResponse<T>> => {
  const timeout = options.timeout || 30000;
  delete options.timeout;

  if (isDebugApiLogEnabled()) {
    const method = (options.method || 'GET').toUpperCase();
    debugApiLog(
      '[API request]',
      method,
      url,
      bodyPreviewForApiLog(options as RequestInit)
    );
  }

  try {
    const response = await fetchWithTimeout(url, options, timeout);

    // 401 에러 처리 (토큰 만료 또는 세션 없음)
    if (response.status === 401) {
      // 공개/비인증 요청(skipAuth)은 로그인 강제 이동하지 않고 호출부에서 처리
      if (options.skipAuth) {
        const publicErrorData = await parseErrorResponse(response);
        const err = new ApiError(
          response.status,
          response.statusText,
          publicErrorData,
          getErrorMessage(new ApiError(response.status, response.statusText, publicErrorData))
        );
        err.skipGlobalLoginRedirect = true;
        throw err;
      }

      // 토큰이 있으면 리프레시 토큰으로 갱신 시도
      let isSessionExpired = false;
      if (retryOn401 && tokenStorage.hasTokens()) {
        const refreshResult = await refreshSession();
        if (refreshResult === 'refreshed') {
          // 토큰 갱신 후 요청 재시도
          const newHeaders = new Headers(options.headers);
          const newToken = tokenStorage.getAccessToken();
          if (newToken) {
            newHeaders.set('Authorization', `Bearer ${newToken}`);
          }
          return executeRequest<T>(url, { ...options, headers: newHeaders }, false);
        }
        if (refreshResult === 'failed') {
          // 네트워크 오류 등 일시적 실패 — 세션은 유지하고 로그인 이동 없이 에러만 전달
          const transientErrorData = await parseErrorResponse(response);
          const transientError = new ApiError(
            response.status,
            response.statusText,
            transientErrorData,
            getErrorMessage(new ApiError(response.status, response.statusText, transientErrorData))
          );
          transientError.skipGlobalLoginRedirect = true;
          throw transientError;
        }
        // 리프레시 토큰까지 만료/무효 — 공개 경로 여부와 무관하게 로그인 필요
        isSessionExpired = true;
      }

      // 오픈클래스·장바구니 등 공개 탐색 중(비로그인 또는 갱신 후에도 401)에는 로그인 페이지로 보내지 않음
      if (!isSessionExpired && typeof window !== 'undefined' && isPublicShoppingBrowsePath(window.location.pathname)) {
        const publicErrorData = await parseErrorResponse(response);
        const err = new ApiError(
          response.status,
          response.statusText,
          publicErrorData,
          getErrorMessage(new ApiError(response.status, response.statusText, publicErrorData))
        );
        err.skipGlobalLoginRedirect = true;
        throw err;
      }

      // 토큰 갱신 실패(세션 만료) 또는 토큰이 없음 - 토큰 정리 후 로그인 페이지로 리다이렉트
      endSessionAndRedirectToLogin();
      // 리다이렉트 후 실행을 중단하기 위해 에러 throw
      // (이동 경로는 위에서 정했으므로 React Query 전역 핸들러가 /login 으로 덮어쓰지 않게 한다)
      const sessionExpiredError = new ApiError(401, 'Unauthorized', undefined, '토큰이 만료되었거나 세션이 없습니다.');
      sessionExpiredError.skipGlobalLoginRedirect = true;
      throw sessionExpiredError;
    }

    // 응답 파싱
    const contentType = response.headers.get('content-type');
    let data: T;
    
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      try {
        data = text ? JSON.parse(text) : ({} as T);
      } catch {
        // JSON 파싱 실패 시 빈 객체로 처리 (status는 response에서 가져옴)
        if (!response.ok) {
          const parseErr = new ApiError(
            response.status,
            response.statusText,
            {},
            getErrorMessage(new ApiError(response.status, response.statusText, {}))
          );
          throw parseErr;
        }
        data = {} as T;
      }
    } else {
      data = (await response.text()) as unknown as T;
    }

    // 에러 응답 처리
    if (!response.ok) {
      const errorData = typeof data === 'object' && data !== null ? data : {};
      const error = new ApiError(
        response.status,
        response.statusText,
        errorData,
        getErrorMessage(new ApiError(response.status, response.statusText, errorData))
      );

      debugApiLog('API Error', url, response.status, errorData);

      throw error;
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (error) {
    // 네트워크 에러 처리
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new ApiError(
        0,
        'Network Error',
        undefined,
        '네트워크 연결을 확인해주세요.'
      );
    }

    // 이미 ApiError인 경우 그대로 throw
    if (error instanceof ApiError) {
      throw error;
    }

    // 기타 에러
    throw new ApiError(
      0,
      'Unknown Error',
      undefined,
      error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'
    );
  }
};

/**
 * API 클라이언트 클래스
 */
class ApiClient {
  /**
   * 요청 헤더 생성
   */
  private getHeaders(options: RequestOptions = {}, body?: any): HeadersInit {
    const headers: Record<string, string> = {};
    
    // 엄격하게: FormData인 경우 Content-Type을 설정하지 않음 (브라우저가 자동으로 boundary 포함하여 설정)
    // FormData가 아닌 경우에만 Content-Type 설정
    const isFormData = body instanceof FormData;
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    // options.headers에 명시적으로 설정된 헤더 병합 (FormData인 경우 Content-Type 제외)
    if (options.headers) {
      const customHeaders = options.headers as Record<string, string>;
      Object.entries(customHeaders).forEach(([key, value]) => {
        // FormData인 경우 Content-Type 헤더는 무시 (브라우저가 자동 설정)
        if (!(isFormData && key.toLowerCase() === 'content-type')) {
          headers[key] = value;
        }
      });
    }

    // 인증 토큰 추가 (skipAuth가 false이고 토큰이 있는 경우)
    if (!options.skipAuth && tokenStorage.hasTokens()) {
      const token = tokenStorage.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * GET 요청
   */
  async get<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    // Query params를 URL에 추가
    let url = `${API_BASE_URL}${endpoint}`;
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          const paramValue = typeof value === 'boolean' ? String(value) : String(value);
          searchParams.append(key, paramValue);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await executeRequest<T>(url, {
      ...options,
      method: 'GET',
      headers: this.getHeaders(options),
    });

    debugApiLog('[API] GET', url, '←', response.data);

    return response.data;
  }

  /**
   * POST 요청
   */
  async post<T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const isFormData = data instanceof FormData;
    const body =
      data === undefined || data === null
        ? undefined
        : isFormData
          ? data
          : serializeJsonRequestBody(data);

    const response = await executeRequest<T>(url, {
      ...options,
      method: 'POST',
      headers: this.getHeaders(options, data),
      body,
    });

    debugApiLog('[API] POST', url, '→', isFormData ? '[FormData]' : data, '←', response.data);

    return response.data;
  }

  /**
   * PUT 요청
   */
  async put<T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await executeRequest<T>(url, {
      ...options,
      method: 'PUT',
      headers: this.getHeaders(options),
      body: serializeJsonRequestBody(data),
    });

    debugApiLog('[API] PUT', url, '→', data, '←', response.data);

    return response.data;
  }

  /**
   * PATCH 요청
   */
  async patch<T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await executeRequest<T>(url, {
      ...options,
      method: 'PATCH',
      headers: this.getHeaders(options),
      body: serializeJsonRequestBody(data),
    });

    debugApiLog('[API] PATCH', url, '→', data, '←', response.data);

    return response.data;
  }

  /**
   * DELETE 요청
   */
  async delete<T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await executeRequest<T>(url, {
      ...options,
      method: 'DELETE',
      headers: this.getHeaders(options),
      body: serializeJsonRequestBody(data),
    });

    debugApiLog('[API] DELETE', url, '←', response.data);

    return response.data;
  }

  /**
   * 토큰 설정
   */
  setTokens(accessToken: string, refreshToken?: string): void {
    tokenStorage.setTokens(accessToken, refreshToken);
  }

  /**
   * 토큰 제거
   */
  clearTokens(): void {
    tokenStorage.clearTokens();
  }

  /**
   * 토큰 확인
   */
  hasTokens(): boolean {
    return tokenStorage.hasTokens();
  }

  /** 저장된 액세스 토큰(토스 결제 승인 fetch 등에서 사용) */
  getAccessToken(): string | null {
    return tokenStorage.getAccessToken();
  }
}

// 싱글톤 인스턴스 export
export const apiClient = new ApiClient();

// 기본 export
export default apiClient;
